package kangaroorewards.appsdk.core.network

import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive

/**
 * Extract a human friendly error message from a JSON response body.
 *
 * Behavior:
 * - If the top-level object has an "error" object with a "message" field, return that message.
 * - Otherwise, iterate the object's keys and collect values (arrays / primitives / nested objects),
 *   return only the values (no keys) joined by new lines.
 * - If parsing fails or nothing useful is found, return the raw responseBody as fallback.
 */
fun extractErrorMessage(responseBody: String): String {
    val jsonElement = try {
        Json.parseToJsonElement(responseBody)
    } catch (_: Exception) {
        // Not valid JSON — return raw body so caller still sees something.
        return responseBody
    }

    if (jsonElement is JsonObject) {
        // Case: {"error": {"message":"..."}}
        jsonElement["error"]?.let { err ->
            when (err) {
                is JsonObject -> {
                    // prefer "message" but fall back to "description" or any primitive inside
                    err["message"]?.let { m ->
                        if (m is JsonPrimitive) return m.content
                    }
                    err["description"]?.let { d ->
                        if (d is JsonPrimitive) return d.content
                    }
                    // If "error" object contains other primitives, return the first primitive string found
                    for ((_, v) in err) {
                        when (v) {
                            is JsonPrimitive -> return v.content
                            is JsonArray, is JsonObject -> {
                                val nested = collectMessages(v)
                                if (nested.isNotEmpty()) return nested.joinToString("\n")
                            }
                        }
                    }
                }
                is JsonPrimitive -> return err.content
                is JsonArray -> {
                    val arr = collectMessages(err)
                    if (arr.isNotEmpty()) return arr.joinToString("\n")
                }
            }
        }

        // Case: {"first_name":["..."], "last_name":["..."]}
        // Collect only the values (no keys) across all fields and return them joined by new line.
        val allMessages = mutableListOf<String>()
        for ((_, value) in jsonElement) {
            val parts = collectMessages(value)
            if (parts.isNotEmpty()) allMessages.addAll(parts)
        }
        if (allMessages.isNotEmpty()) {
            return allMessages.joinToString("\n")
        }
    }

    // fallback to returning the raw body
    return responseBody
}

private fun collectMessages(element: JsonElement): List<String> = when (element) {
    is JsonArray -> element.flatMap { collectMessages(it) }
    is JsonObject -> {
        // if it has a "message" primitive, return it first
        element["message"]?.let { m ->
            if (m is JsonPrimitive) return listOf(m.content)
        }
        // otherwise collect recursively from children
        element.flatMap { (_, v) -> collectMessages(v) }
    }
    is JsonPrimitive -> listOf(element.content)
    else -> emptyList()
}
